import { salutationFor } from "@signalist/shared-types";
import { MouseEvent as ReactMouseEvent } from "react";

import { format } from "date-fns";

import { faCircle } from "@fortawesome/pro-regular-svg-icons";

import { TableColumn } from "@/components/Table/Table";
import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { ProspectScoreBadge } from "@/components/ProspectScoreBadge/ProspectScoreBadge";
import { CategorySegmentCell } from "@/components/CategoryReasoningPopover/CategorySegmentCell";
import { MyConnections } from "@/pages/Public/pages/Login/schema/login";

import { EnrichedNameCell, UnenrichedNameCell } from "./LinkedinConnectionsNameCell";
import { ConnectionCompanyCell } from "./LinkedinConnectionsCompanyCell";

export interface BuildColumnsOpts {
	selectedIds: number[];
	onSelectRow: (id: number) => void;
	selectAll: boolean;
	onToggleSelectAll: () => void;
	enrichEnabled: boolean;
	onEnrichRow: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
}

function renderSelectColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: "",
		uid: "connectionSelect",
		allowExclude: false,
		calcWidth: 62,
		locked: "leftFirst",
		hideable: false,
		moveable: false,
		show: true,
		onHeaderClick: opts.onToggleSelectAll,
		render: (row: MyConnections, index?: number) => {
			const selected = opts.selectedIds.includes(row.id);

			return (
				<div className="group relative w-5 h-5 flex items-center justify-center">
					<span
						className={`absolute inset-0 flex items-center justify-center text-xs font-medium text-table-header ${selected ? "hidden" : "group-hover:hidden"}`}
					>
						{typeof index !== "undefined" ? index + 1 : ""}
					</span>
					<div
						className={`absolute inset-0 flex items-center justify-center ${selected ? "" : "hidden group-hover:flex"}`}
					>
						<Checkbox isChecked={selected} onChange={() => opts.onSelectRow(row.id)} />
					</div>
				</div>
			);
		},
	};
}

function renderScoreColumn(): TableColumn {
	return {
		title: "",
		width: "fit-content",
		calcWidth: 54,
		uid: "connectionScore",
		locked: "leftSecond",
		hideable: false,
		moveable: false,
		show: true,
		render: (row: MyConnections) => {
			if (!row.overallScore) {
				return <Icon className="h-[6px] opacity-[0.5]" color="#9CA3AF" icon={faCircle} />;
			}

			return <ProspectScoreBadge overallScore={row.overallScore} />;
		},
	};
}

function renderNameColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("name"),
		width: "fit-content",
		minWidth: "260px",
		calcWidth: 260,
		uid: "connectionName",
		locked: "leftThird",
		hideable: true,
		moveable: false,
		show: true,
		render: (row: MyConnections) =>
			row.isEnriched && row.prospectId ? (
				<EnrichedNameCell handleCopyClick={opts.handleCopyClick} row={row} ts={opts.ts} />
			) : (
				<UnenrichedNameCell
					enrichEnabled={opts.enrichEnabled}
					row={row}
					ts={opts.ts}
					onEnrichRow={opts.onEnrichRow}
				/>
			),
	};
}

function renderCompanyColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("companyName"),
		width: "fit-content",
		minWidth: "270px",
		calcWidth: 270,
		uid: "connectionCompany",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<ConnectionCompanyCell handleCopyClick={opts.handleCopyClick} row={row} />
		),
	};
}

function renderJobTitleColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("jobTitle"),
		width: "fit-content",
		minWidth: "150px",
		uid: "connectionTitle",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.title) {
						opts.handleCopyClick(row.title);
					}
				}}
			>
				{row.title || "-"}
			</button>
		),
	};
}

function renderConnectedOnColumn(ts: (key: string) => string): TableColumn {
	return {
		title: ts("connectedOn"),
		width: "fit-content",
		uid: "connectionConnectedAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<span className="text-sm text-gray-500">
				{row.connectedAt ? format(new Date(row.connectedAt), "dd.MM.yyyy") : ""}
			</span>
		),
	};
}

function renderCountryColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.personalCountry"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.country) {
						opts.handleCopyClick(row.country);
					}
				}}
			>
				{row.country || "-"}
			</button>
		),
	};
}

function renderLanguageColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.language"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionLanguage",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.language) {
						opts.handleCopyClick(row.language);
					}
				}}
			>
				{row.language || "-"}
			</button>
		),
	};
}

function renderSeniorityColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.seniority"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionSeniority",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<span className="text-sm text-gray-700">{row.seniority || "-"}</span>
		),
	};
}

function renderGenderColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.gender"),
		width: "fit-content",
		minWidth: "100px",
		uid: "connectionGender",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<span className="text-sm text-gray-700">{row.gender || "-"}</span>
		),
	};
}

function renderSalutationDeColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.salutationDe"),
		width: "fit-content",
		minWidth: "80px",
		uid: "connectionSalutationDe",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => {
			return <span className="text-sm text-gray-700">{salutationFor(row, "de") || "-"}</span>;
		},
	};
}

function renderSalutationEnColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.salutationEn"),
		width: "fit-content",
		minWidth: "80px",
		uid: "connectionSalutationEn",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => {
			return <span className="text-sm text-gray-700">{salutationFor(row, "en") || "-"}</span>;
		},
	};
}

function renderLocationColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.location"),
		width: "fit-content",
		minWidth: "140px",
		uid: "connectionLocation",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => {
			const location = [row.city, row.country].filter(Boolean).join(", ") || "-";

			return (
				<button
					className="text-sm text-gray-700 truncate cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (location !== "-") {
							opts.handleCopyClick(location);
						}
					}}
				>
					{location}
				</button>
			);
		},
	};
}

function renderCompanyCountryColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.companyCountry"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionCompanyCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization?.country) {
						opts.handleCopyClick(row.organization.country);
					}
				}}
			>
				{row.organization?.country || "-"}
			</button>
		),
	};
}

function renderCompanyIndustryColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.companyIndustry"),
		width: "fit-content",
		minWidth: "130px",
		uid: "connectionCompanyIndustry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization?.industry) {
						opts.handleCopyClick(row.organization.industry);
					}
				}}
			>
				{row.organization?.industry || "-"}
			</button>
		),
	};
}

function renderCompanySizeColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.companySize"),
		width: "fit-content",
		minWidth: "110px",
		uid: "connectionCompanySize",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization?.estimatedNumEmployees) {
						opts.handleCopyClick(row.organization.estimatedNumEmployees.toLocaleString());
					}
				}}
			>
				{row.organization?.estimatedNumEmployees?.toLocaleString() || "-"}
			</button>
		),
	};
}

function renderCompanyFoundedYearColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.companyFoundedYear"),
		width: "fit-content",
		minWidth: "110px",
		uid: "connectionCompanyFoundedYear",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<span className="text-sm text-gray-700">{row.organization?.foundedYear ?? "-"}</span>
		),
	};
}

function renderPersonaCategoryColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.personaCategory"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionPersonaCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<CategorySegmentCell
				categoryName={row.scorePersonaCategoryResult?.scorePersonaCategory?.category}
				kind="persona"
				prioScore={row.scorePersonaCategoryResult?.personaScore}
				reasoning={row.scorePersonaCategoryResult?.reason}
			/>
		),
	};
}

function renderCompanyCategoryColumn(opts: BuildColumnsOpts): TableColumn {
	return {
		title: opts.ts("myConnections.companyCategory"),
		width: "fit-content",
		minWidth: "120px",
		uid: "connectionCompanyCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: MyConnections) => (
			<CategorySegmentCell
				categoryName={row.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory?.category}
				kind="company"
				prioScore={row.scorePersonaCategoryResult?.companyScore}
				reasoning={row.organization?.scoreCompanyCategoryResult?.reason}
			/>
		),
	};
}

export function buildConnectionColumns(opts: BuildColumnsOpts): TableColumn[] {
	return [
		renderSelectColumn(opts),
		renderScoreColumn(),
		renderNameColumn(opts),
		renderCompanyColumn(opts),
		renderJobTitleColumn(opts),
		renderConnectedOnColumn(opts.ts),
		renderCountryColumn(opts),
		renderLanguageColumn(opts),
		renderSeniorityColumn(opts),
		renderGenderColumn(opts),
		renderSalutationDeColumn(opts),
		renderSalutationEnColumn(opts),
		renderLocationColumn(opts),
		renderCompanyCountryColumn(opts),
		renderCompanyIndustryColumn(opts),
		renderCompanySizeColumn(opts),
		renderCompanyFoundedYearColumn(opts),
		renderPersonaCategoryColumn(opts),
		renderCompanyCategoryColumn(opts),
	];
}
